// PREZENCE — Add Employee Page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';

export default function AddEmployee() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name:'', email:'', role:'employee', department_id:'', designation:'',
    employee_id:'', phone:'', shift_start:'09:00', shift_end:'18:00', is_remote: false,
  });
  const [saving, setSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [departments, setDepartments] = useState([]);

  // Departments are structured now — load them for the dropdown.
  useEffect(() => {
    api.get('/departments')
      .then(r => setDepartments(r.data.departments || []))
      .catch(() => {});
  }, []);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/auth/register', form);
      // SEC-2: backend now returns a unique random temp password. Show it to the
      // admin so they can hand it over — it won't be retrievable again.
      const temp = res.data.temp_password;
      if (temp) {
        setTempPassword(temp);   // display in the copy-able panel below
      } else {
        toast.success(res.data.message || 'Employee added!');
        navigate('/admin/employees');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to add employee');
    } finally { setSaving(false); }
  };

  // After successful registration, show the one-time temp password to hand over.
  if (tempPassword) {
    return (
      <div style={{ animation:'fadeIn 0.4s ease', maxWidth:560, margin:'40px auto' }}>
        <div className="card" style={{ textAlign:'center', padding:'40px 32px' }}>
          <div style={{ fontSize:44, marginBottom:12 }}>✅</div>
          <h1 className="page-title" style={{ marginBottom:8 }}>Employee Added</h1>
          <p className="page-sub" style={{ marginBottom:24 }}>
            Share this temporary password with the employee. They'll be asked to change it
            on first login. <strong style={{ color:'#f59e0b' }}>This is shown only once.</strong>
          </p>
          <div style={{
            background:'rgba(99,220,188,.08)', border:'1px solid rgba(99,220,188,.3)',
            borderRadius:12, padding:'18px 20px', marginBottom:20,
            fontFamily:'JetBrains Mono, monospace', fontSize:24, fontWeight:700,
            letterSpacing:'.06em', color:'#63dcbc', userSelect:'all',
          }}>{tempPassword}</div>
          <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
            <button className="btn-secondary" onClick={() => { navigator.clipboard?.writeText(tempPassword); toast.success('Copied'); }}>Copy password</button>
            <button className="btn-primary" onClick={() => navigate('/admin/employees')}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ animation:'fadeIn 0.4s ease', maxWidth:680, margin:'0 auto' }}>
      <div className="page-header">
        <h1 className="page-title">Add Employee</h1>
        <p className="page-sub">Register a new employee. A secure temporary password will be generated and shown once.</p>
      </div>
      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">FULL NAME *</label>
              <input className="form-input" value={form.full_name} onChange={e=>set('full_name',e.target.value)} placeholder="e.g. Arun Kumar" required />
            </div>
            <div className="form-group">
              <label className="form-label">EMAIL ADDRESS *</label>
              <input className="form-input" type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="arun@prelegant.com" required />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">ROLE</label>
              <select className="form-select" value={form.role} onChange={e=>set('role',e.target.value)}>
                <option value="employee">Employee</option>
                <option value="hr">HR</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">EMPLOYEE ID</label>
              <input className="form-input" value={form.employee_id} onChange={e=>set('employee_id',e.target.value)} placeholder="e.g. EMP001" />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">DEPARTMENT</label>
              <select className="form-select" value={form.department_id} onChange={e=>set('department_id',e.target.value)}>
                <option value="">Select department…</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">DESIGNATION</label>
              <input className="form-input" value={form.designation} onChange={e=>set('designation',e.target.value)} placeholder="e.g. Frontend Developer" />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">PHONE</label>
              <input className="form-input" value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="+91 9876543210" />
            </div>
            <div className="form-group">
              <label className="form-label">WORKER TYPE</label>
              <select className="form-select" value={form.is_remote} onChange={e=>set('is_remote',e.target.value==='true')}>
                <option value="false">In-office</option>
                <option value="true">Remote</option>
              </select>
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">SHIFT START</label>
              <input className="form-input" type="time" value={form.shift_start} onChange={e=>set('shift_start',e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">SHIFT END</label>
              <input className="form-input" type="time" value={form.shift_end} onChange={e=>set('shift_end',e.target.value)} />
            </div>
          </div>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/employees')}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <><div className="spinner" style={{width:14,height:14}} /> Adding...</> : 'Add Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
